"use client";

import {
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { Comment, CommentCategory } from "@/lib/types";
import { getCommentAnchorId, parseCommentTargetId } from "@/lib/comment-links";
import {
  getCommentRevealServerSnapshot,
  getCommentRevealSnapshot,
  subscribeCommentReveal,
} from "@/lib/comment-reveal";
import { planCommentReveal, type CommentRevealPlan } from "@/lib/comment-tree";

/** 강조 유지 시간(ms). 이후 강조 클래스가 빠지면서 `CommentItem` 의 transition 으로 자연 소멸한다. */
export const COMMENT_DEEP_LINK_HIGHLIGHT_MS = 2500;

/**
 * 앵커가 DOM 에 나타나길 기다리는 최대 프레임 수(≈1초 @60fps).
 * 탭·visibleCount 를 올린 뒤 다음 커밋이면 앵커가 생기므로 보통 1~2프레임에 끝난다.
 * 그래도 안 생기면(섹션이 언마운트된 경우 등) 조용히 포기한다.
 */
const MAX_ANCHOR_WAIT_FRAMES = 60;

export interface UseCommentDeepLinkOptions {
  /** 섹션의 루트 댓글 배열 (표시 순서 그대로) */
  comments: Comment[];
  /** 최초 로드 중이면 true. false 로 바뀐 뒤에야 해시를 해석한다 */
  loading: boolean;
  /** 섹션의 "더보기" 단위(`COMMENTS_PER_PAGE`). 더보기 없는 섹션은 생략(전부 그리므로 1) */
  pageSize?: number;
  /** 루트 댓글 → 탭 카테고리. 섹션의 필터와 같은 함수를 넘긴다. 탭 없는 섹션은 생략 */
  resolveCategory?: (comment: Comment) => CommentCategory;
  /** 이 섹션이 탭으로 노출하는 카테고리. 대상 카테고리가 여기 없으면(예: 포럼의 `chat`) 무시한다 */
  allowedCategories?: readonly CommentCategory[];
  /** 현재 활성 탭. 탭 없는 섹션은 생략 */
  activeTab?: CommentCategory;
  /** 탭 전환 setter. 탭 없는 섹션은 생략 */
  setActiveTab?: (category: CommentCategory) => void;
  /** 섹션의 `visibleCount` setter — 탭 전환과 같은 배치에서 갱신된다. 더보기 없는 섹션은 생략 */
  setVisibleCount?: Dispatch<SetStateAction<number>>;
  /**
   * 해시 대상이 로드된 트리에 없을 때 섹션이 목록을 다시 읽는 함수. 대상당 한 번만 부르고, 끝나면 노출을 다시 시도한다 —
   * 같은 페이지를 보는 동안 달린 새 댓글의 알림을 벨에서 연 경우(JCG-3615). 생략하면 트리에 없는 대상은 그냥 무시된다.
   */
  onMissingTarget?: (commentId: string) => Promise<unknown>;
}

export interface CommentDeepLinkState {
  /** 현재 해시가 가리키고 이 섹션에서 노출 가능한 댓글 id. 없으면 null */
  targetCommentId: string | null;
  /** 지금 강조 중인 댓글 id (스크롤 직후 약 2.5초). 없으면 null */
  highlightedCommentId: string | null;
}

function subscribeToHash(onChange: () => void): () => void {
  window.addEventListener("hashchange", onChange);
  window.addEventListener("popstate", onChange);
  // Next `<Link>`/`router.push` 의 같은 페이지 해시 이동은 pushState 만 하고 hashchange 를 쏘지 않는다.
  // Navigation API 가 있는 브라우저에서는 currententrychange 로 그 경우도 잡는다(없으면 조용히 생략).
  const navigation = (window as Window & { navigation?: EventTarget }).navigation;
  navigation?.addEventListener("currententrychange", onChange);
  return () => {
    window.removeEventListener("hashchange", onChange);
    window.removeEventListener("popstate", onChange);
    navigation?.removeEventListener("currententrychange", onChange);
  };
}

const getHashSnapshot = () => window.location.hash;
const getServerHashSnapshot = () => "";

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** 섹션이 대상 루트를 화면에 올리도록 탭·visibleCount 를 같은 배치에서 갱신한다. */
function applyRevealPlan(
  plan: CommentRevealPlan,
  { activeTab, setActiveTab, setVisibleCount }: Pick<
    UseCommentDeepLinkOptions,
    "activeTab" | "setActiveTab" | "setVisibleCount"
  >
) {
  if (plan.category !== null && setActiveTab && plan.category !== activeTab) {
    // 탭이 바뀌면 탭 클릭 리셋처럼 새로 세되 대상이 보이는 최소치로
    setActiveTab(plan.category);
    setVisibleCount?.(plan.minVisibleCount);
  } else {
    // 같은 탭이면 사용자가 이미 더 펼친 값을 줄이지 않는다
    setVisibleCount?.((prev) => Math.max(prev, plan.minVisibleCount));
  }
}

/**
 * 댓글 딥링크(`…#comment-{uuid}`) 진입·해시 변경 시 대상 댓글을 화면에 올리는 훅 (JCG-3603).
 *
 * 흐름: `loading=false` 이거나 해시가 바뀌면 → `parseCommentTargetId(location.hash)` → `planCommentReveal`
 * → 탭·visibleCount 를 같은 배치로 올리고 → 앵커(`comment-{id}`)가 DOM 에 생기면
 * `scrollIntoView({ block: "center" })` + 강조(약 2.5초 뒤 해제).
 *
 * 폴백(아무 것도 하지 않음): 규약에 안 맞는 해시, 이 섹션 트리에 없는 id(부재·다른 slug), 이 섹션 탭에 없는
 * 카테고리. 삭제된 댓글은 트리에 남아 있으므로 그대로 이동·강조해 "삭제된 댓글입니다" 자리를 보여준다.
 *
 * 노출은 "해시 진입/변경당 1회" 이벤트다 — 댓글 좋아요·추가로 `comments` 가 바뀌어도 다시 실행하지 않아
 * 사용자가 옮긴 탭을 되돌리지 않는다(최신 계획·setter 는 `useEffectEvent` 로 읽는다).
 */
export function useCommentDeepLink({
  comments,
  loading,
  pageSize = 1,
  resolveCategory,
  allowedCategories,
  activeTab,
  setActiveTab,
  setVisibleCount,
  onMissingTarget,
}: UseCommentDeepLinkOptions): CommentDeepLinkState {
  const hash = useSyncExternalStore(subscribeToHash, getHashSnapshot, getServerHashSnapshot);
  const hashTargetId = useMemo(() => parseCommentTargetId(hash), [hash]);

  // 같은 해시를 다시 요청하면(알림 벨 재클릭) hashchange 가 나지 않으므로 명시적 노출 요청의 nonce 를 노출 이펙트
  // 의존성에 넣어 다시 돌린다. 요청 id 가 현재 해시와 다르면 무시한다(해시가 SSOT, JCG-3601).
  const revealRequest = useSyncExternalStore(
    subscribeCommentReveal,
    getCommentRevealSnapshot,
    getCommentRevealServerSnapshot,
  );
  const revealNonce =
    revealRequest && revealRequest.id === hashTargetId ? revealRequest.nonce : 0;

  // 해시가 가리키는 댓글이 이 섹션 트리에 있을 때의 계획(트리 탐색은 여기 한 번만 — 다른 slug 의 댓글이면 null).
  const treePlan = useMemo(() => {
    if (loading || !hashTargetId) return null;
    return planCommentReveal({
      roots: comments,
      targetId: hashTargetId,
      categoryOf: resolveCategory,
      pageSize,
    });
  }, [comments, loading, hashTargetId, resolveCategory, pageSize]);

  // 이 섹션의 탭으로 노출 가능할 때만 실제 계획이 된다(예: 포럼 섹션에 없는 `chat` 은 무시).
  const tabAllowed =
    !treePlan ||
    treePlan.category === null ||
    !allowedCategories ||
    allowedCategories.includes(treePlan.category);
  const plan = tabAllowed ? treePlan : null;

  // 대상이 트리에 없으면(탭 불허는 제외) 섹션의 재조회를 대상당 한 번 부르고, 끝나면 노출 이펙트를 다시 돌린다.
  const [refetchNonce, setRefetchNonce] = useState(0);
  const refetchedTargetRef = useRef<string | null>(null);
  const refetchMissingTarget = useEffectEvent(async (commentId: string) => {
    if (!onMissingTarget) return;
    await onMissingTarget(commentId);
    setRefetchNonce((nonce) => nonce + 1);
  });
  useEffect(() => {
    if (loading || !hashTargetId || treePlan) return;
    if (refetchedTargetRef.current === hashTargetId) return;
    refetchedTargetRef.current = hashTargetId;
    void refetchMissingTarget(hashTargetId);
  }, [loading, hashTargetId, treePlan]);

  const targetCommentId = plan ? plan.targetId : null;

  const [highlighted, setHighlighted] = useState<string | null>(null);

  const revealTarget = useEffectEvent((commentId: string): boolean => {
    if (!plan || plan.targetId !== commentId) return false;
    applyRevealPlan(plan, { activeTab, setActiveTab, setVisibleCount });
    return true;
  });

  useEffect(() => {
    if (loading || !hashTargetId) return;
    if (!revealTarget(hashTargetId)) return;

    const anchorId = getCommentAnchorId(hashTargetId);
    let frame = 0;
    let attempts = 0;
    let clearTimer: number | undefined;

    const tryScroll = () => {
      const anchor = document.getElementById(anchorId);
      if (!anchor) {
        attempts += 1;
        if (attempts < MAX_ANCHOR_WAIT_FRAMES) frame = requestAnimationFrame(tryScroll);
        return;
      }
      anchor.scrollIntoView({
        block: "center",
        behavior: prefersReducedMotion() ? "auto" : "smooth",
      });
      setHighlighted(hashTargetId);
      clearTimer = window.setTimeout(() => {
        setHighlighted((current) => (current === hashTargetId ? null : current));
      }, COMMENT_DEEP_LINK_HIGHLIGHT_MS);
    };

    // 탭·visibleCount 가 커밋된 다음 프레임부터 앵커를 찾는다.
    frame = requestAnimationFrame(tryScroll);

    return () => {
      cancelAnimationFrame(frame);
      if (clearTimer !== undefined) window.clearTimeout(clearTimer);
    };
  }, [loading, hashTargetId, revealNonce, refetchNonce]);

  return {
    targetCommentId,
    // 해시가 다른 곳으로 옮겨가면 타이머와 무관하게 즉시 강조를 거둔다.
    highlightedCommentId: highlighted !== null && highlighted === targetCommentId ? highlighted : null,
  };
}
